/**
 * The app's one JWT payload decoder.
 *
 * The parsing itself is `jwt-decode`'s — the library Auth0's own SPA docs point
 * at for this. We used to hand-roll the base64url padding restore and the UTF-8
 * reassembly, in two copies that were each broken in a way the other was not
 * (one threw on any payload whose length was not a multiple of four, the other
 * read `atob` output as latin1 and mangled every non-ASCII name). Neither is
 * code worth owning.
 *
 * Decoding is not verification. The signature is checked by the backend
 * through JWKS; nothing read out of here is trusted as an authorization
 * decision on the server.
 */
import { jwtDecode } from 'jwt-decode';

/** Custom-claim namespace — matches the backend `ns` in auth.ts and the
 *  PostLogin action in `infrastructure/auth0/tenant/actions/`. */
export const CLAIM_NAMESPACE = 'https://nebari.bonsaibar.com';

/** The access-token claim carrying the viewer's raw Auth0 role names. */
export const ROLES_CLAIM = `${CLAIM_NAMESPACE}/roles`;

/** The access-token claim carrying the viewer's email, used as the display
 *  fallback when the `/staff/me` profile is unavailable. */
export const EMAIL_CLAIM = `${CLAIM_NAMESPACE}/email`;

/**
 * Decode a JWT's payload segment, or `null` when the token is absent,
 * malformed or not parseable as a JSON object. `null` rather than `{}` so a
 * caller can tell "no claims to read" from "claims read, and this one is
 * missing" — the distinction BON-558's roles claim turns on.
 */
export function decodeJwtPayload(token: string | undefined): Record<string, unknown> | null {
  if (!token) return null;
  try {
    const parsed: unknown = jwtDecode(token);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Read a claim that should hold an array of strings. Returns `null` when the
 *  claim is ABSENT — which is a different state from an empty array, and the
 *  caller is expected to treat it as such. */
export function readStringArrayClaim(
  claims: Record<string, unknown> | null,
  name: string,
): string[] | null {
  const value = claims?.[name];
  if (!Array.isArray(value)) return null;
  return value.filter((v): v is string => typeof v === 'string');
}
